
import { ref, onMounted } from "vue"
import router from '@/router'

export default {
  name: "BankDetailView",
  props: ['baseUrl'],
  setup(props: any) {
    // State
    const isEditing = ref(false)
    const loading = ref(false)
    const message = ref('')
    const messageType = ref('')
    const officerId = ref<number | null>(null)
    const bankAccountId = ref<number | null>(null)
    
    // Form data - matches BankDetailViewModel
    const form = ref({
      id: null as number | null,
      accountTitle: '',
      sortCode: '',
      accountNumber: '',
      securityOfficerId: null as number | null
    })
    
    const errors = ref<any>({})

    // Helper function to get OfficerId from localStorage
    const getOfficerIdFromStorage = () => {
      const storedOfficerId = localStorage.getItem("OfficerId")
      if (!storedOfficerId) {
        message.value = 'Officer ID not found. Please log in again.'
        messageType.value = 'alert-danger'
        setTimeout(() => {
          router.push('/login')
        }, 2000)
        return null
      }
      return parseInt(storedOfficerId)
    }

    // Format sort code as user types (adds hyphens automatically)
    const formatSortCode = () => {
      let value = form.value.sortCode.replace(/\D/g, '') // Remove non-digits
      if (value.length > 6) {
        value = value.slice(0, 6)
      }
      // Add hyphens
      if (value.length >= 5) {
        form.value.sortCode = `${value.slice(0, 2)}-${value.slice(2, 4)}-${value.slice(4, 6)}`
      } else if (value.length >= 3) {
        form.value.sortCode = `${value.slice(0, 2)}-${value.slice(2, 4)}`
      } else if (value.length >= 2) {
        form.value.sortCode = `${value.slice(0, 2)}`
      } else {
        form.value.sortCode = value
      }
    }

    // Format account number (only digits, max 8)
    const formatAccountNumber = () => {
      form.value.accountNumber = form.value.accountNumber.replace(/\D/g, '').slice(0, 8)
    }

    // Clean sort code for API (remove hyphens, get just digits as number)
    const cleanSortCodeForApi = (sortCode: string): number => {
      const cleaned = sortCode.replace(/\D/g, '')
      return parseInt(cleaned, 10)
    }

    // Clean account number for API (just digits as number)
    const cleanAccountNumberForApi = (accountNumber: string): number => {
      const cleaned = accountNumber.replace(/\D/g, '')
      return parseInt(cleaned, 10)
    }

    // Format sort code for display (add hyphens to numeric value)
    const formatSortCodeForDisplay = (sortCode: number): string => {
      const str = sortCode.toString().padStart(6, '0')
      if (str.length === 6) {
        return `${str.slice(0, 2)}-${str.slice(2, 4)}-${str.slice(4, 6)}`
      }
      return str
    }

    // Load bank account if editing - you'll need a GET endpoint for this
    const loadBankAccount = async (id: number) => {
      loading.value = true
      message.value = ''
      
      //console.log("test test");
      
      try {
        const token = localStorage.getItem("Guardtoken")
        // You need a GET endpoint to fetch bank account details
        // For now, we'll assume you have one
        const response = await fetch(`${props.baseUrl}SecurityOfficers/GetSecurityOfficerBankDetail/${id}`, {
          method: 'GET',
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept": "application/json"
          }
        })
        
        if (response.ok) {
          const data = await response.json()
          form.value = {
            id: data.id || data.iD || data.ID,
            accountTitle: data.accountTitle || '',
            sortCode: formatSortCodeForDisplay(data.sortCode),
            accountNumber: data.accountNumber?.toString() || '',
            securityOfficerId: data.securityOfficerId
          }
        } else {
          console.error('Load error:', await response.text())
          message.value = 'Bank account not found'
          messageType.value = 'alert-danger'
        }
      } catch (error) {
        console.error('Error loading bank account:', error)
        message.value = 'Failed to load bank account'
        messageType.value = 'alert-danger'
      } finally {
        loading.value = false
      }
    }

    // Validate form
    const validateForm = () => {
      errors.value = {}
      
      if (!form.value.accountTitle?.trim()) {
        errors.value.accountTitle = 'Account title is required'
      } else if (form.value.accountTitle.length > 100) {
        errors.value.accountTitle = 'Account title must not exceed 100 characters'
      }
      
      // Validate sort code (with hyphens)
      const cleanSortCode = form.value.sortCode.replace(/\D/g, '')
      if (!form.value.sortCode?.trim()) {
        errors.value.sortCode = 'Sort code is required'
      } else if (cleanSortCode.length !== 6) {
        errors.value.sortCode = 'Sort code must be 6 digits (format: 00-00-00)'
      }
      
      // Validate account number
      const cleanAccountNumber = form.value.accountNumber.replace(/\D/g, '')
      if (!form.value.accountNumber?.trim()) {
        errors.value.accountNumber = 'Account number is required'
      } else if (cleanAccountNumber.length !== 8) {
        errors.value.accountNumber = 'Account number must be 8 digits'
      }
      
      return Object.keys(errors.value).length === 0
    }

    // Submit form - Using POST as specified in your API
    const submitForm = async () => {
      if (!validateForm()) {
        return
      }

      loading.value = true
      message.value = ''
      
      // Prepare payload matching BankDetailViewModel
      const payload = {
        ID: form.value.id || 0,  // Note: Capital ID as in your ViewModel
        AccountTitle: form.value.accountTitle.trim(),
        SortCode: cleanSortCodeForApi(form.value.sortCode),
        AccountNumber: cleanAccountNumberForApi(form.value.accountNumber),
        SecurityOfficerId: officerId.value
      }
      
      console.log('Sending payload to API:', payload)
      
      try {
        const token = localStorage.getItem("Guardtoken")
        
        // Using POST method as specified in your [HttpPost] attribute
        const response = await fetch(`${props.baseUrl}SecurityOfficers/UpdateSecurityOfficerBankDetail`, {
          method: 'POST',  // Your API uses POST
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify(payload)
        })
        
        const responseText = await response.text()
        console.log('Response status:', response.status)
        console.log('Response body:', responseText)
        
        let data = null
        try {
          data = responseText ? JSON.parse(responseText) : null
        } catch (e) {
          console.error('Failed to parse JSON:', e)
        }
        
        if (response.ok) {
          message.value = 'Bank account saved successfully!'
          messageType.value = 'alert-success'
          
          // Reset form and redirect after success
          setTimeout(() => {
            //router.push('/bank-accounts')
            router.push('/documents')
          }, 1500)
        } else {
          if (response.status === 400) {
            message.value = 'Invalid data provided. Please check your input.'
            messageType.value = 'alert-danger'
          } else if (response.status === 401) {
            message.value = 'Unauthorized. Please log in again.'
            messageType.value = 'alert-danger'
            setTimeout(() => {
              router.push('/login')
            }, 2000)
          } else {
            message.value = data?.message || `Operation failed with status: ${response.status}`
            messageType.value = 'alert-danger'
          }
        }
      } catch (error) {
        console.error('Error in submitForm:', error)
        message.value = 'Network error. Please check your connection and try again.'
        messageType.value = 'alert-danger'
      } finally {
        loading.value = false
      }
    }

    // Reset form
    const resetForm = () => {
      if (isEditing.value && bankAccountId.value) {
        loadBankAccount(bankAccountId.value)
      } else {
        form.value = {
          id: null,
          accountTitle: '',
          sortCode: '',
          accountNumber: '',
          securityOfficerId: officerId.value
        }
      }
      errors.value = {}
      message.value = ''
    }

    // Go back to bank accounts list
    const goBack = () => {
      //router.push('/bank-accounts')
      router.push('/documents')
    }

    // Lifecycle
    onMounted(() => {
      //console.log("onMounted onMounted");

      officerId.value = getOfficerIdFromStorage()
      //console.log("officerId" ,  officerId.value);
      if (officerId.value) 
        loadBankAccount(officerId.value)

    })

    return {
      form,
      errors,
      loading,
      message,
      messageType,
      officerId,
      isEditing,
      formatSortCode,
      formatAccountNumber,
      submitForm,
      resetForm,
      goBack
    }
  }
}
